import { ReactElement, useEffect, useState, useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { history } from 'umi';
import { useDispatch } from 'dva';
import { Col, Row, Tooltip, Statistic } from 'antd';
import Context from '@/utils/context';
import { Typography, Button, StatusBox, Modal, message } from 'iglooform';
import './quotes-list-item.less';
import { TooltipFilled, ArrowRightOutlined } from 'iglooicon';
import { images } from '@/assets/images';
import { getImageUrl } from '@/service';
import { QuotationStatus, QuotationStatusType } from '@/utils/status';
import moment from 'moment';

const currentLang = localStorage.getItem('selected_lang');

const QuotesListItem = (props: any): ReactElement => {
  const { quotesListItem, userInfo } = props;
  const { formatMessage } = useIntl();
  const [url, setUrl] = useState<string>('');
  const { isPc } = useContext(Context);
  const dispatch = useDispatch();
  const goToHome = () => {
    dispatch({
      type: 'global/setData',
      payload: {
        menuKey: 'home',
      },
    });
  };

  const paymentExpiredConfirm = (value: any) => {
    const startDate: any = value.policy_start_at * 1000;
    if (+moment().startOf('day').format('x') < startDate) {
      history.push({
        pathname: `/home/${value.product_type}/payment`,
        query: {
          applicationId: value?.application_id,
          productKey: value?.product_key,
          planKey: value?.plan_key,
          quotationId: value.ext_id,
          policyStartDate: startDate,
          rePay: 'true',
        },
      });
      goToHome();
      return;
    }
    Modal.confirm({
      title: formatMessage({ id: 'Start Date Expired' }),
      width: 600,
      closable: true,
      maskClosable: false,
      visible: true,
      content: (
        <Typography level="body1">
          {formatMessage({
            id: 'The policy start date is expired, the system will automatically modify your start date to today and the end date will extend respectively.',
          })}
        </Typography>
      ),
      okText: 'Got it',
      cancelText: 'No',
      onOk: () => {
        history.push({
          pathname: `/home/${value.product_type}/payment`,
          query: {
            applicationId: value?.application_id,
            productKey: value?.product_key,
            planKey: value?.plan_key,
            quotationId: value?.ext_id,
            policyStartDate: startDate,
            rePay: 'true',
          },
        });
        goToHome();
      },
    });
  };

  useEffect(() => {
    if (
      quotesListItem?.logo_url &&
      quotesListItem?.logo_url?.includes(API_URL)
    ) {
      getImageUrl(quotesListItem?.logo_url).then((res) => {
        setUrl(res?.url);
      });
    }
    setUrl(quotesListItem?.logo_url);
  }, []);

  if (!isPc) {
    return (
      <div className="listBox-h5">
        {url && <img src={url || images.tob_icon} alt="" className="icon" />}
        <div className="rightDetail">
          <Typography
            level="h4"
            wrapElement="div"
            style={{ marginBottom: '8px' }}
          >
            {quotesListItem.plan_name}
          </Typography>
          <Typography
            level="body2"
            wrapElement="div"
            style={{ marginBottom: '8px' }}
          >
            <Statistic
              value={quotesListItem.premium}
              prefix={formatMessage({ id: 'VND' })}
              groupSeparator={currentLang === 'vi-VN' ? '.' : ','}
              decimalSeparator={currentLang === 'vi-VN' ? ',' : '.'}
            />
          </Typography>
          <div style={{ marginBottom: '8px' }}>
            <StatusBox
              type={QuotationStatusType[quotesListItem?.state]}
              className="state"
            >
              {QuotationStatus[quotesListItem?.state] ? (
                <FormattedMessage {...QuotationStatus[quotesListItem?.state]} />
              ) : (
                quotesListItem?.state
              )}
            </StatusBox>
          </div>
          {quotesListItem.reject_reason && (
            <Tooltip title={quotesListItem.reject_reason}>
              <TooltipFilled className="rejectIcon" />
            </Tooltip>
          )}
          {quotesListItem.state === 'QuoteCreated' &&
            !quotesListItem.product_name.includes('BSH Domestic travel') && (
              <Button
                type="primary"
                onClick={() => {
                  if (userInfo?.status === 2) {
                    message.error(
                      formatMessage({
                        id: 'Your account has been deactivated, please contact your upline manager who you worked.',
                      }),
                    );
                    return;
                  }
                  history.push({
                    pathname: `/home/${quotesListItem.product_type}/single-quote`,
                    query: {
                      productKey: quotesListItem.product_key,
                      planKey: quotesListItem.plan_key,
                      quotationId: quotesListItem.ext_id,
                    },
                  });
                  goToHome();

                  // event tracking: confirm quote
                  window.gtag('event', 'confirm_quote', {
                    quote_id: quotesListItem.ext_id,
                    product_key: quotesListItem.product_key,
                    plan_key: quotesListItem.plan_key,
                    user_unique_id: JSON.parse(
                      localStorage.getItem('user_info') ?? '{}',
                    )?.id,
                  });
                }}
                className="button"
              >
                {formatMessage({ id: 'Confirm Quote' })}
              </Button>
            )}
          {quotesListItem.state === 'QuotePreSubmitted' &&
            !quotesListItem.product_name.includes('BSH Domestic travel') && (
              <Button
                type="primary"
                onClick={() => {
                  if (userInfo?.status === 2) {
                    message.error(
                      formatMessage({
                        id: 'Your account has been deactivated, please contact your upline manager who you worked.',
                      }),
                    );
                    return;
                  }
                  history.push({
                    pathname: `/home/${quotesListItem.product_type}/application`,
                    query: {
                      productKey: quotesListItem.product_key,
                      planKey: quotesListItem.plan_key,
                      quotationId: quotesListItem.ext_id,
                    },
                  });
                  goToHome();

                  // event tracking: re-complete_application
                  window.gtag('event', 're_complete_application', {
                    quote_id: quotesListItem.ext_id,
                    product_key: quotesListItem.product_key,
                    plan_key: quotesListItem.plan_key,
                    user_unique_id: JSON.parse(
                      localStorage.getItem('user_info') ?? '{}',
                    )?.id,
                  });
                }}
                className="button"
              >
                {formatMessage({ id: 'Complete Application' })}
              </Button>
            )}
          {(quotesListItem.state === 'QuotePaymentPending' ||
            quotesListItem.state === 'QuotePaymentFailed') &&
            !quotesListItem.product_name.includes('BSH Domestic travel') && (
              <Button
                type="primary"
                onClick={() => {
                  if (userInfo?.status === 2) {
                    message.error(
                      formatMessage({
                        id: 'Your account has been deactivated, please contact your upline manager who you worked.',
                      }),
                    );
                    return;
                  }
                  paymentExpiredConfirm(quotesListItem);
                }}
                className="button"
              >
                {formatMessage({ id: 'Make Payment' })}
              </Button>
            )}
        </div>
        <div className="rightIcon">
          <ArrowRightOutlined
            className="icon"
            onClick={() =>
              history.push(
                '/policies/detail?tabKey=quote&id=' +
                  (quotesListItem.id || quotesListItem.id) +
                  '&product_type=' +
                  quotesListItem.product_type +
                  '&product_key=' +
                  quotesListItem.product_key +
                  '&plan_key=' +
                  quotesListItem.plan_key,
              )
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="listBox">
      <div className="listTopBox">
        {url && <img src={url || images.tob_icon} alt="" className="icon" />}
        <Typography level="h4" className="product">
          {quotesListItem.plan_name}
        </Typography>
        <StatusBox
          type={QuotationStatusType[quotesListItem?.state]}
          className="state"
        >
          {QuotationStatus[quotesListItem?.state] ? (
            <FormattedMessage {...QuotationStatus[quotesListItem?.state]} />
          ) : (
            quotesListItem?.state
          )}
        </StatusBox>
        {quotesListItem.reject_reason && (
          <Tooltip title={quotesListItem.reject_reason}>
            <TooltipFilled className="rejectIcon" />
          </Tooltip>
        )}
        {quotesListItem.state === 'QuoteCreated' &&
          !quotesListItem.product_name.includes('BSH Domestic travel') && (
            <Button
              type="primary"
              onClick={() => {
                if (userInfo?.status === 2) {
                  message.error(
                    formatMessage({
                      id: 'Your account has been deactivated, please contact your upline manager who you worked.',
                    }),
                  );
                  return;
                }
                history.push({
                  pathname: `/home/${quotesListItem.product_type}/single-quote`,
                  query: {
                    productKey: quotesListItem.product_key,
                    planKey: quotesListItem.plan_key,
                    quotationId: quotesListItem.ext_id,
                  },
                });
                goToHome();

                // event tracking: confirm quote
                window.gtag('event', 'confirm_quote', {
                  quote_id: quotesListItem.ext_id,
                  product_key: quotesListItem.product_key,
                  plan_key: quotesListItem.plan_key,
                  user_unique_id: JSON.parse(
                    localStorage.getItem('user_info') ?? '{}',
                  )?.id,
                });
              }}
              className="button"
            >
              {formatMessage({ id: 'Confirm Quote' })}
            </Button>
          )}
        {quotesListItem.state === 'QuotePreSubmitted' &&
          !quotesListItem.product_name.includes('BSH Domestic travel') && (
            <Button
              type="primary"
              onClick={() => {
                if (userInfo?.status === 2) {
                  message.error(
                    formatMessage({
                      id: 'Your account has been deactivated, please contact your upline manager who you worked.',
                    }),
                  );
                  return;
                }
                history.push({
                  pathname: `/home/${quotesListItem.product_type}/application`,
                  query: {
                    productKey: quotesListItem.product_key,
                    planKey: quotesListItem.plan_key,
                    quotationId: quotesListItem.ext_id,
                  },
                });
                goToHome();

                // event tracking: re-complete_application
                window.gtag('event', 're_complete_application', {
                  quote_id: quotesListItem.ext_id,
                  product_key: quotesListItem.product_key,
                  plan_key: quotesListItem.plan_key,
                  user_unique_id: JSON.parse(
                    localStorage.getItem('user_info') ?? '{}',
                  )?.id,
                });
              }}
              className="button"
            >
              {formatMessage({ id: 'Complete Application' })}
            </Button>
          )}
        {(quotesListItem.state === 'QuotePaymentPending' ||
          quotesListItem.state === 'QuotePaymentFailed') &&
          !quotesListItem.product_name.includes('BSH Domestic travel') && (
            <Button
              type="primary"
              onClick={() => {
                if (userInfo?.status === 2) {
                  message.error(
                    formatMessage({
                      id: 'Your account has been deactivated, please contact your upline manager who you worked.',
                    }),
                  );
                  return;
                }
                paymentExpiredConfirm(quotesListItem);
              }}
              className="button"
            >
              {formatMessage({ id: 'Make Payment' })}
            </Button>
          )}
        <Button
          onClick={() =>
            history.push(
              '/policies/detail?tabKey=quote&id=' +
                (quotesListItem.id || quotesListItem.id) +
                '&product_type=' +
                quotesListItem.product_type +
                '&product_key=' +
                quotesListItem.product_key +
                '&plan_key=' +
                quotesListItem.plan_key,
            )
          }
          className="button"
        >
          {formatMessage({ id: 'View Details' })}
        </Button>
      </div>
      <Row className="listBottomBox">
        <Col span={7}>
          <Typography
            level="body1"
            style={{ display: 'block', marginBottom: '5px', color: '#666666' }}
          >
            {formatMessage({ id: 'Quote ID' })}
          </Typography>
          <Typography level="body1">{quotesListItem.display_id}</Typography>
        </Col>
        <Col span={7}>
          <Typography
            level="body1"
            style={{ display: 'block', marginBottom: '5px', color: '#666666' }}
          >
            {formatMessage({ id: 'Premium' })}
          </Typography>
          <Typography level="body1">
            <Statistic
              value={quotesListItem.premium}
              prefix={formatMessage({ id: 'VND' })}
              groupSeparator={currentLang === 'vi-VN' ? '.' : ','}
              decimalSeparator={currentLang === 'vi-VN' ? ',' : '.'}
            />
          </Typography>
        </Col>
        <Col span={7}>
          <Typography
            level="body1"
            style={{ display: 'block', marginBottom: '5px', color: '#666666' }}
          >
            {formatMessage({ id: 'Expire Date' })}
          </Typography>
          <Typography level="body1">
            {moment(quotesListItem?.expire_at * 1000).format('DD / MM / YYYY')}
          </Typography>
        </Col>
      </Row>
    </div>
  );
};

export default QuotesListItem;
